import type { DatePresetKey } from '../../utils/date-presets';
import type { FilterEntry } from '../../components/EntityFilterPicker';

export const DASHBOARD_SCHEMA_VERSION = 1 as const;

export type PanelViz = 'table' | 'line' | 'bar' | 'pie' | 'stat' | 'summary';

export interface PanelQuery {
  group_by: string;
  /** Entity filters (offer/flow/traffic-source/LP/network). */
  filters?: FilterEntry[];
  metrics: string[];
  /** Metrics plotted against a second (right) Y axis — for mixing magnitudes. */
  y2_metrics?: string[];
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  limit?: number;
  /** Default true — bots skew every rate. */
  exclude_bots?: boolean;
  conv_time_basis?: 'conversion' | 'click';
  /** When set, this panel uses its own range instead of the dashboard's. */
  time_preset?: DatePresetKey;
  /** Per-panel report timezone (UTC offset hours); absent = the viewer's report-default tz. */
  timezone?: number;
  /** Pin this panel to a specific workspace; absent = follow the viewer's selection. */
  workspace_id?: string;
  /** Show change vs the preceding equal-length period. */
  compare?: boolean;
}

export interface PanelLayout { x: number; y: number; w: number; h: number; }

export interface DashboardPanel {
  id: string;
  title: string;
  subtitle?: string;
  viz: PanelViz;
  query: PanelQuery;
  layout: PanelLayout;
}

/** The jsonb `config` blob of a dashboards row (name/description are columns). */
export interface DashboardConfig {
  version: number;
  /** Relative only — resolved to concrete bounds at query time. */
  time_preset: DatePresetKey;
  timezone?: number;
  panels: DashboardPanel[];
}

/** A full dashboards row as returned by the API. */
export interface Dashboard {
  id: string;
  name: string;
  description?: string | null;
  config: DashboardConfig;
  shared: boolean;
  is_owner: boolean;
  owner_name?: string | null;
  owner_email?: string | null;
  updated_at?: string;
}

export function uid(prefix = 'p'): string {
  const raw = globalThis.crypto?.randomUUID?.() ?? `${Math.random().toString(36)}${Date.now().toString(36)}`;
  return `${prefix}_${raw.replace(/[^a-z0-9]/gi, '').slice(0, 12)}`;
}

export function emptyConfig(): DashboardConfig {
  return { version: DASHBOARD_SCHEMA_VERSION, time_preset: 'last_7_days', panels: [] };
}
